import moment from 'moment';

import {
    defaultBriefOutlineContent,
    defaultExtractContentEditorContent,
    editorTypes,
} from '../constants/tiptapEditorConstants';

/**
 * Refactor the editor version to a unified format based on type
 * @param {object} version - The version object to be refactored
 * @param {string} type - The type of the version (optimizer or outline)
 * @returns {object} - Refactored version object
 */
export const refactorEditorVersion = (version, type) => {
    switch (type) {
        case editorTypes.OPTIMIZER:
            return refactorOptimizerVersion(version);
        case editorTypes.OUTLINE:
            return refactorOutlineVersion(version);
        default:
            return version;
    }
};

/**
 * Parse the editor content safely
 * @param {string} content - The content string to be parsed
 * @param {string} type - The type of the content (optimizer or outline)
 * @returns {object} - Parsed content or default content if parsing fails
 */
export const parseEditorContent = (content, type) => {
    try {
        return JSON.parse(content);
    } catch {
        switch (type) {
            case editorTypes.OPTIMIZER:
                return defaultExtractContentEditorContent;
            case editorTypes.OUTLINE:
                return defaultBriefOutlineContent;
            default:
                return defaultExtractContentEditorContent;
        }
    }
};

/**
 * Refactor an optimizer version
 * @param {object} version - The optimizer version object
 * @returns {object} - Refactored optimizer version object
 */
const refactorOptimizerVersion = (version) => {
    return {
        id: version.id,
        created_at: version.created_at,
        active: version.active,
        name: version.name,
        content: parseEditorContent(version.content),
        word_count: version.word_count,
        readability: version.readability,
        rankability_score: version.rankability_score,
        is_ai_generated: version.is_ai_generated,
    };
};

/**
 * Refactor an outline version
 * @param {object} version - The outline version object
 * @returns {object} - Refactored outline version object
 */
const refactorOutlineVersion = (version) => ({
    id: version.id,
    created_at: version.created_at,
    active: version.active,
    name: version.name,
    content: parseEditorContent(version.outline, editorTypes.OUTLINE),
    progress: Math.ceil((version.progress || 0) * 100),
    word_count: version.word_count,
    readability: null,
    is_ai_generated: version.is_ai_generated,
});

/**
 * Extract basic data from the version
 * @param {object} version - The version object
 * @returns {object} - Basic data from the version
 */
export const getVersionBasicData = (version) => ({
    rankability_score: version.rankability_score,
    word_count: version.word_count,
    readability: version.readability,
    optimal_readability: version.optimal_readability,
    optimal_rankability_score: version.optimal_rankability_score,
    optimal_word_count: version.optimal_word_count,
});

/**
 * Get the currently active version from a list of versions
 * @param {object[]} versions - List of version objects
 * @returns {object} - The currently active version object
 */
export const getCurrentVersion = (versions) => versions.find((version) => version.active);

/**
 * Get sorted collection of versions by descending date
 * @param {object[]} versions - List of version objects
 * @returns {object[]} - Sorted list by descending date
 */
export const sortVersionsByDescendingDate = (versions = []) =>
    versions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

/**
 * Generates a timestamp-based version name or returns the custom one if it is present.
 * @param {object} version - The version object.
 * @param {string} [version.name] - The name of the version.
 * @param {boolean} [version.is_ai_generated=false] - Indicates if the version is AI-generated.
 * @param {string} [version.created_at] - Timestamp of the version creation.
 * @returns {string} Version name.
 */
export const setupVersionName = (version = {}) => {
    const { name, is_ai_generated = false, created_at } = version;

    if (name) return name;

    const createdTime = moment.utc(created_at).local().fromNow();

    return is_ai_generated ? `AI version created ${createdTime}` : `Version created ${createdTime}`;
};
